import * as React from 'react';
import clsx from 'clsx';
// the app-import-free slice of `src/i18n` (see i18n-context.tsx)
import { useTranslations } from 'i18n/use-translations';
import { DismissLayerContext, useDismissLayer } from '../../hooks/use-dismiss-layer';
import { useLatestRef } from '../../hooks/use-latest-ref';
import { sanitiseDomId } from '../../utils/dom-id';
import { ButtonIcon } from '../button-icon';
import { IconLoader } from '../iconLoader';
import { Text } from '../text';
import { InsideDrawerContext } from './drawer-context';
import styles from './drawer.module.css';

export type DrawerSide = 'left' | 'right';
export type DrawerHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface DrawerProps {
    open: boolean;
    onClose: () => void;
    /** Visible heading that names the dialog; it takes focus on open. */
    title: string;
    /** Level of the title heading, to fit the page outline. */
    headingLevel?: DrawerHeadingLevel;
    children: React.ReactNode;
    footer?: React.ReactNode;
    side?: DrawerSide;
    /** Focus target on close; defaults to whatever had focus when the drawer opened. */
    returnFocusRef?: React.RefObject<HTMLElement>;
    /** The scrolling body, for callers that scroll their content programmatically. */
    bodyRef?: React.Ref<HTMLDivElement>;
    className?: string;
    bodyClassName?: string;
    footerClassName?: string;
    testId?: string;
}

/** Non-modal side drawer: the page behind stays interactive and Tab is never trapped. */
export const Drawer: React.FC<DrawerProps> = (props) => {
    const { open, onClose, title, headingLevel = 2, children, footer, side = 'right', returnFocusRef, bodyRef } = props;
    const { className, bodyClassName, footerClassName, testId } = props;

    const { __ } = useTranslations();
    const titleId = `${sanitiseDomId(React.useId())}-drawer-title`;
    const rootRef = React.useRef<HTMLDivElement>(null);
    const titleRef = React.useRef<HTMLHeadingElement>(null);
    const latestReturnFocusRef = useLatestRef(returnFocusRef);

    React.useEffect(() => {
        if (!open) {
            return undefined;
        }

        const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const root = rootRef.current;

        titleRef.current?.focus();

        return () => {
            const active = document.activeElement;
            const target = latestReturnFocusRef.current?.current ?? opener;

            // Only reclaim focus the drawer held; a user who has moved on into the page keeps their place.
            if ((active === null || active === document.body || root?.contains(active)) && target?.isConnected) {
                target.focus();
            }
        };
    }, [open, latestReturnFocusRef]);

    // A container layer: popups inside it, and controls that handle Escape themselves, keep it.
    const dismissLayer = useDismissLayer({
        enabled: open,
        onDismiss: onClose,
        getElements: () => [rootRef.current],
        kind: 'container',
    });

    if (!open) {
        return null;
    }

    const childTestId = (suffix: string): string | undefined =>
        testId === undefined ? undefined : `${testId}-${suffix}`;
    const Heading = `h${headingLevel}` as const;

    return (
        <div
            ref={rootRef}
            role="dialog"
            aria-labelledby={titleId}
            data-side={side}
            data-testid={testId}
            className={clsx(styles['drawer'], className)}
        >
            <div className={styles['drawer-header']}>
                <Heading ref={titleRef} id={titleId} tabIndex={-1} className={styles['drawer-title']}>
                    <Text as="span" variant="title-large">
                        {title}
                    </Text>
                </Heading>
                <ButtonIcon
                    size="sm"
                    color="ghost"
                    pill={false}
                    className={styles['drawer-close']}
                    ariaLabel={__('misc.close')}
                    testId={childTestId('close-button')}
                    onClick={onClose}
                >
                    <IconLoader iconName="IconCancel" size="md" />
                </ButtonIcon>
            </div>
            <InsideDrawerContext.Provider value={true}>
                <div
                    ref={bodyRef}
                    className={clsx(styles['drawer-body'], bodyClassName)}
                    data-testid={childTestId('body')}
                >
                    <DismissLayerContext.Provider value={dismissLayer}>{children}</DismissLayerContext.Provider>
                </div>
                {footer !== undefined && (
                    <div className={clsx(styles['drawer-footer'], footerClassName)} data-testid={childTestId('footer')}>
                        <DismissLayerContext.Provider value={dismissLayer}>{footer}</DismissLayerContext.Provider>
                    </div>
                )}
            </InsideDrawerContext.Provider>
        </div>
    );
};
